import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useI18n } from '@react/hooks';
import { ErrorAlert } from '@react/components';
import { ZONE2_NAV_ITEMS } from '@/features/layout/logic';
import { searchPeople } from '@/features/chat/logic';
import { EntityAvatar } from './EntityAvatar';
import { useOpenDirectConversation } from '@/features/chat/useOpenDirectConversation';
import type { PickerCandidate } from '@/features/userprofile/pickerCandidates';

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

const PEOPLE_DEBOUNCE_MS = 250;
const PEOPLE_LIMIT = 5;

interface SearchModalProps {
  onClose: () => void;
}

export function SearchModal({ onClose }: SearchModalProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const auth = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [people, setPeople] = useState<PickerCandidate[]>([]);
  const [isSearchingPeople, setIsSearchingPeople] = useState(false);
  const searchSeq = useRef(0);
  const dm = useOpenDirectConversation();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // People search — debounced like UserPicker's: one request per pause, and a
  // sequence guard so a slow early answer never overwrites a later one.
  useEffect(() => {
    const q = query.trim();
    if (!q) {
      searchSeq.current += 1;
      setPeople([]);
      setIsSearchingPeople(false);
      return;
    }
    const seq = ++searchSeq.current;
    setIsSearchingPeople(true);
    const timer = window.setTimeout(() => {
      searchPeople(q, PEOPLE_LIMIT)
        .then((results) => {
          if (seq !== searchSeq.current) return;
          setPeople(results.filter(p => p.profileId !== auth.activeProfileId));
        })
        .catch(() => {
          if (seq !== searchSeq.current) return;
          setPeople([]);
        })
        .finally(() => {
          if (seq === searchSeq.current) setIsSearchingPeople(false);
        });
    }, PEOPLE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query, auth.activeProfileId]);

  const filteredItems = query
    ? ZONE2_NAV_ITEMS.filter((item) =>
        t(item.labelKey).toLowerCase().includes(query.toLowerCase())
      )
    : ZONE2_NAV_ITEMS;

  const handleSelect = (href: string) => {
    navigate(href);
    onClose();
  };

  const handlePickPerson = async (candidate: PickerCandidate) => {
    if (await dm.open(candidate.profileId)) onClose();
  };

  const showPeopleGroup = people.length > 0 || isSearchingPeople;

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center pt-[20vh]">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        className="relative w-full max-w-lg mx-4 bg-card rounded-lg border border-border shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-label={t('layout.search.title')}
      >
        <div className="flex items-center gap-2 px-4 border-b border-border">
          <SearchIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { dm.clearError(); setQuery(e.target.value); }}
            placeholder={t('layout.search.placeholder')}
            className="flex-1 h-12 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
          />
          <kbd className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">ESC</kbd>
        </div>
        <div className="max-h-80 overflow-y-auto p-2">
          {dm.error ? (
            <div className="mb-2">
              <ErrorAlert error={dm.error} dismissible onDismiss={dm.clearError} />
            </div>
          ) : null}

          {showPeopleGroup && (
            <div className="mb-1">
              <p className="px-3 pb-1 pt-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('layout.search.people')}
              </p>
              {isSearchingPeople ? (
                <div className="space-y-1 px-1 pb-1">
                  {Array.from({ length: 2 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3 px-2 py-2">
                      <div className="h-8 w-8 animate-pulse rounded-full bg-muted" />
                      <div className="flex-1 space-y-1.5">
                        <div className="h-3 w-28 animate-pulse rounded bg-muted" />
                        <div className="h-2.5 w-20 animate-pulse rounded bg-muted" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <ul>
                  {people.map((person) => (
                    <li key={person.profileId}>
                      <button
                        type="button"
                        disabled={dm.isOpening}
                        onClick={() => handlePickPerson(person)}
                        className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-accent disabled:opacity-60"
                      >
                        <EntityAvatar name={person.displayName} image={person.avatar} profileId={person.profileId} size="sm" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-foreground">{person.displayName}</span>
                          {person.secondary && (
                            <span className="block truncate text-xs text-muted-foreground">{person.secondary}</span>
                          )}
                        </span>
                        {dm.pendingProfileId === person.profileId && (
                          <span className="h-3.5 w-3.5 flex-shrink-0 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {filteredItems.length > 0 && (
            <div>
              {showPeopleGroup && (
                <p className="px-3 pb-1 pt-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('layout.search.pages')}
                </p>
              )}
              {filteredItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleSelect(item.href)}
                  className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm text-foreground hover:bg-accent transition-colors text-left"
                >
                  <span className="text-muted-foreground">{t(item.labelKey)}</span>
                </button>
              ))}
            </div>
          )}

          {!showPeopleGroup && filteredItems.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">{t('layout.search.noResults')}</p>
          )}
        </div>
      </div>
    </div>
  );
}
